import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { showNotification, useQueryFetch } from 'sonata-react-components';

import { copyToClipboard } from '@/components/reporting/helpers/reporting.helpers';
import {
  deleteShareReport,
  getShareReports,
  updateShareReportEnabled
} from '@/components/reporting/reporting.resources';
import { buildSharedReportLink } from '@/helpers/shareReport/shareReport.helper';
import { ShareReportList } from '../../ShareReportList';

import type { SharedReportType } from 'sonata-static-storage/lib/schemas/report/sharedReport.schema';
import type { ShareReportListItem } from '../../ShareReportList';

type Props = {
  lineItemIds: string[];
  reloadToken?: string;
  sharedReportId?: string;
  type: Extract<SharedReportType, 'singleDriveToStore' | 'agregatedDriveToStore' | 'singleDooh'>;
};

export default function SharedReportList({ lineItemIds, reloadToken, sharedReportId, type }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const { data, refetch } = useQueryFetch<ShareReportListItem[]>(
    ['driveToStore-sharedReports', lineItemIds.join(','), type],
    async () => await getShareReports(lineItemIds, type),
    { enabled: lineItemIds.length > 0 }
  );

  const [reports, setReports] = useState<ShareReportListItem[] | null>(null);
  const [mutating, setMutating] = useState(false);

  useEffect(() => {
    if (data) {
      setReports(data);
    }
  }, [data]);

  useEffect(() => {
    if (reloadToken) {
      refetch();
    }
  }, [reloadToken, refetch]);

  const onToggleEnabled = useCallback(async (id: string, enabled: boolean) => {
    setMutating(true);
    try {
      await updateShareReportEnabled(id, enabled);
      setReports(prev => prev?.map(report => (report._id === id ? { ...report, enabled } : report)) ?? null);
    } finally {
      setMutating(false);
    }
  }, []);

  const onDelete = useCallback(async (id: string) => {
    setMutating(true);
    try {
      await deleteShareReport(id);
      setReports(prev => prev?.filter(report => report._id !== id) ?? null);
    } finally {
      setMutating(false);
    }
  }, []);

  const onCopy = useCallback(
    (id: string) => {
      copyToClipboard(buildSharedReportLink(id));
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'The link has been copied correctly',
          id: 'sharedReport.list.shareLink.copiedToast'
        }),
        {
          closable: true,
          icon: 'fal fa-check-circle'
        }
      );
    },
    [formatMessage]
  );

  return (
    <ShareReportList
      activeItemId={sharedReportId}
      items={reports}
      loading={mutating}
      onCopy={onCopy}
      onDelete={onDelete}
      onToggleEnabled={onToggleEnabled}
    />
  );
}
